import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SERVICE_AREAS = [
  'Greater Metropolitan Area',
  'Suburban Communities',
  'Downtown District',
  'Historic Neighborhoods',
  'Waterfront Properties',
];

const CONTACT_METHODS = [
  { value: 'phone', label: 'Phone Call' },
  { value: 'email', label: 'Email' },
  { value: 'text', label: 'Text Message' },
  { value: 'any', label: 'Any — I am flexible' },
];

const ENQUIRY_TYPES = [
  { value: 'home_valuation', label: 'I want a free home valuation' },
  { value: 'selling_advice', label: 'I need advice about selling my home' },
  { value: 'ready_to_sell', label: 'I am ready to list my property' },
  { value: 'downsizing', label: 'I am considering downsizing' },
  { value: 'relocation', label: 'I am relocating and need to sell' },
  { value: 'inherited_property', label: 'I have inherited a property to sell' },
  { value: 'previously_unsold', label: 'My home did not sell before — help' },
  { value: 'general', label: 'General enquiry' },
];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const hp = formData.get('phone_alt');
    if (hp && typeof hp === 'string' && hp.trim() !== '') {
      setSubmitted(true);
      setSubmitting(false);
      return;
    }
    formData.delete('phone_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9gfj24ugrdi4rjg57fg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries()).map(([k, v]) => [k, String(v)])
        ).toString(),
      });
      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && (serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam'));

      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        setSubmitted(true);
        form.reset();
        setCharCount(0);
      } else {
        setFormError(isSpam ? 'Your submission could not be processed. Please try again.' : (serverMsg || 'Something went wrong. Please try again.'));
      }
    } catch {
      setFormError('Unable to connect. Please check your internet and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden bg-foreground-950">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20modern%20suburban%20home%20exterior%20at%20golden%20hour%2C%20warm%20sunlight%2C%20professional%20real%20estate%20photography%2C%20clean%20driveway%2C%20manicured%20landscaping%2C%20inviting%20front%20porch%2C%20soft%20warm%20neutral%20tones%20with%20subtle%20green%20accents%2C%20editorial%20quality&width=1600&height=600&seq=contact-hero-01&orientation=landscape"
            alt="Beautiful home exterior"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/80 to-foreground-950/90" />
        <div className="relative max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10 py-16 md:py-24 lg:py-28 text-center">
          <p className="text-accent-300 font-body text-sm tracking-[0.2em] uppercase mb-4">
            Contact
          </p>
          <h1 className="font-heading text-background-50 text-3xl md:text-4xl lg:text-5xl xl:text-6xl mb-5 max-w-[800px] mx-auto leading-tight">
            Let's Talk About Your Property Plans
          </h1>
          <p className="text-background-100/70 font-body text-base md:text-lg max-w-[600px] mx-auto leading-relaxed">
            Whether you are ready to sell now or simply exploring your options, Carol is here to listen,
            advise and guide you through every step of the process.
          </p>
        </div>
      </section>

      {/* ===== Contact Content ===== */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">

            {/* Left: Contact Info */}
            <div className="lg:w-[380px] flex-shrink-0 space-y-8">
              {/* Direct Contact Card */}
              <div className="bg-background-100/70 rounded-xl border border-background-200/70 p-6">
                <h3 className="font-heading text-foreground-900 text-lg mb-5">
                  Get in Touch Directly
                </h3>
                <div className="space-y-5">
                  {/* Phone */}
                  <a
                    href="tel:+19734934311"
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                      <i className="ri-phone-line text-primary-600 text-lg" />
                    </div>
                    <div>
                      <p className="text-foreground-700 font-body text-xs font-medium tracking-wide uppercase mb-0.5">
                        Telephone
                      </p>
                      <p className="text-foreground-900 font-body text-sm font-semibold group-hover:text-primary-600 transition-colors">
                        +1 (973) 493-4311
                      </p>
                      <p className="text-foreground-500 font-body text-xs mt-0.5">
                        Available during business hours
                      </p>
                    </div>
                  </a>

                  {/* Email */}
                  <a
                    href="mailto:CarolBerry.Realtor@gmail.com"
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-50 flex-shrink-0">
                      <i className="ri-mail-line text-accent-700 text-lg" />
                    </div>
                    <div>
                      <p className="text-foreground-700 font-body text-xs font-medium tracking-wide uppercase mb-0.5">
                        Email
                      </p>
                      <p className="text-foreground-900 font-body text-sm font-semibold group-hover:text-primary-600 transition-colors">
                        CarolBerry.Realtor@gmail.com
                      </p>
                      <p className="text-foreground-500 font-body text-xs mt-0.5">
                        Carol typically responds within 24 hours
                      </p>
                    </div>
                  </a>

                  {/* Century 21 */}
                  <a
                    href="http://carolberry.C21.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-50 flex-shrink-0">
                      <i className="ri-building-line text-secondary-700 text-lg" />
                    </div>
                    <div>
                      <p className="text-foreground-700 font-body text-xs font-medium tracking-wide uppercase mb-0.5">
                        Brokerage
                      </p>
                      <p className="text-foreground-900 font-body text-sm font-semibold group-hover:text-primary-600 transition-colors">
                        Century 21 Cedarcrest Realty, Inc.
                      </p>
                      <p className="text-foreground-500 font-body text-xs mt-0.5">
                        View Carol's profile
                      </p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Response Promise */}
              <div className="bg-primary-50/50 rounded-xl border border-primary-100 p-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0 mt-0.5">
                    <i className="ri-time-line text-primary-600 text-sm" />
                  </div>
                  <h4 className="font-heading text-foreground-900 text-base">
                    Quick Response Promise
                  </h4>
                </div>
                <p className="text-foreground-600 font-body text-sm leading-relaxed">
                  Carol understands that timing matters when you are considering selling your home.
                  Every enquiry receives a personal response, usually within one business day.
                </p>
              </div>

              {/* Service Areas */}
              <div className="bg-background-100/70 rounded-xl border border-background-200/70 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-map-pin-line text-primary-500" />
                  <h4 className="font-heading text-foreground-900 text-base">
                    Service Areas
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_AREAS.map((area) => (
                    <span
                      key={area}
                      className="inline-flex items-center px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-foreground-700 font-body text-xs"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>

              {/* Quick CTA */}
              <Link
                to="/home-valuation"
                className="flex items-center gap-3 p-5 bg-foreground-950 rounded-xl group transition-all hover:bg-foreground-900"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 flex-shrink-0">
                  <i className="ri-home-4-line text-background-50 text-lg" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-background-50 font-body text-sm font-semibold">
                    Request a Free Home Valuation
                  </p>
                  <p className="text-background-100/50 font-body text-xs">
                    Get an estimate of your property's market value
                  </p>
                </div>
                <i className="ri-arrow-right-line text-background-100/50 group-hover:text-background-50 group-hover:translate-x-1 transition-all" />
              </Link>
            </div>

            {/* Right: Contact Form */}
            <div className="flex-1 min-w-0">
              {submitted ? (
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-8 md:p-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-50 mx-auto mb-6">
                    <i className="ri-check-line text-primary-500 text-3xl" />
                  </div>
                  <h3 className="font-heading text-foreground-900 text-2xl md:text-3xl mb-3">
                    Thank You — Carol Will Be in Touch
                  </h3>
                  <p className="text-foreground-600 font-body text-sm md:text-base leading-relaxed max-w-[480px] mx-auto mb-8">
                    Your property details have been received. Carol will contact you to learn more about
                    your plans and explain the next steps.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
                    >
                      <i className="ri-home-line text-base" />
                      Return to Homepage
                    </Link>
                    <Link
                      to="/blog"
                      className="inline-flex items-center justify-center gap-2 border border-background-300 text-foreground-700 px-6 py-3 rounded-md text-sm font-body font-semibold whitespace-nowrap hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-article-line text-base" />
                      Browse Seller Resources
                    </Link>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="contact-form"
                  className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8"
                >
                  <div className="mb-8">
                    <h3 className="font-heading text-foreground-900 text-xl md:text-2xl mb-2">
                      Send Carol a Message
                    </h3>
                    <p className="text-foreground-500 font-body text-sm">
                      Fill in the details below and Carol will respond as soon as possible.
                    </p>
                  </div>

                  {/* Row 1: Name + Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-foreground-700 font-body text-sm font-medium mb-1.5"
                      >
                        Full Name *
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        autoComplete="name"
                        className="w-full border border-background-300 rounded-lg px-4 py-3 text-sm font-body text-foreground-900 placeholder-foreground-400 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-foreground-700 font-body text-sm font-medium mb-1.5"
                      >
                        Email Address *
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        autoComplete="email"
                        className="w-full border border-background-300 rounded-lg px-4 py-3 text-sm font-body text-foreground-900 placeholder-foreground-400 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  {/* Row 2: Phone + Preferred Contact */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label
                        htmlFor="contact-phone"
                        className="block text-foreground-700 font-body text-sm font-medium mb-1.5"
                      >
                        Telephone Number
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        name="phone"
                        autoComplete="tel"
                        className="w-full border border-background-300 rounded-lg px-4 py-3 text-sm font-body text-foreground-900 placeholder-foreground-400 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
                        placeholder="Your phone number"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-pref"
                        className="block text-foreground-700 font-body text-sm font-medium mb-1.5"
                      >
                        Preferred Contact Method
                      </label>
                      <select
                        id="contact-pref"
                        name="preferred_contact"
                        className="w-full border border-background-300 rounded-lg px-4 py-3 text-sm font-body text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
                      >
                        <option value="">Select a method</option>
                        {CONTACT_METHODS.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 3: Property Address + Enquiry Type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label
                        htmlFor="contact-address"
                        className="block text-foreground-700 font-body text-sm font-medium mb-1.5"
                      >
                        Property Address
                      </label>
                      <input
                        id="contact-address"
                        type="text"
                        name="property_address"
                        autoComplete="street-address"
                        className="w-full border border-background-300 rounded-lg px-4 py-3 text-sm font-body text-foreground-900 placeholder-foreground-400 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
                        placeholder="123 Main Street, City, State"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-enquiry"
                        className="block text-foreground-700 font-body text-sm font-medium mb-1.5"
                      >
                        How Can Carol Help?
                      </label>
                      <select
                        id="contact-enquiry"
                        name="enquiry_type"
                        className="w-full border border-background-300 rounded-lg px-4 py-3 text-sm font-body text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
                      >
                        <option value="">Select an option</option>
                        {ENQUIRY_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-1.5">
                      <label
                        htmlFor="contact-message"
                        className="block text-foreground-700 font-body text-sm font-medium"
                      >
                        Your Message *
                      </label>
                      <span className={`text-xs font-body ${charCount > 450 ? 'text-primary-600' : 'text-foreground-400'}`}>
                        {charCount}/500
                      </span>
                    </div>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      required
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-background-300 rounded-lg px-4 py-3 text-sm font-body text-foreground-900 placeholder-foreground-400 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all resize-none"
                      placeholder="Tell Carol about your property, your timeline and what you would like to achieve..."
                    />
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />

                  {/* Consent */}
                  <div className="mb-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="consent"
                        required
                        className="mt-0.5 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                      />
                      <span className="text-foreground-600 font-body text-xs leading-relaxed">
                        I consent to Carol Berry contacting me about my enquiry.
                        My information will be handled in accordance with applicable privacy regulations.
                        I understand this is a no-obligation conversation.
                      </span>
                    </label>
                  </div>

                  {/* Error */}
                  {formError && (
                    <div className="mb-5 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <i className="ri-error-warning-line text-primary-600 mt-0.5 flex-shrink-0" />
                        <p className="text-primary-700 font-body text-sm">{formError}</p>
                      </div>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-8 py-4 rounded-lg text-base font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg" />
                        Sending your message...
                      </>
                    ) : (
                      <>
                        Send Message to Carol
                        <i className="ri-send-plane-line text-lg" />
                      </>
                    )}
                  </button>

                  <p className="mt-4 text-center text-foreground-400 font-body text-xs">
                    Your information is secure and never shared with third parties.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Service Areas Detail ===== */}
      <section className="py-14 md:py-20 bg-background-100/70">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="lg:w-1/2">
              <p className="text-primary-500 font-body text-sm tracking-widest uppercase mb-3">
                Local Expertise
              </p>
              <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl lg:text-4xl mb-4">
                Carol Serves Your Community
              </h2>
              <p className="text-foreground-600 font-body text-sm md:text-base leading-relaxed mb-6">
                With deep knowledge of the local market, buyer demand and neighborhood trends,
                Carol provides accurate valuations and strategic advice tailored to your area.
                Whether you are in the city centre, the suburbs or a surrounding community,
                Carol is ready to help.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  'Local market analysis',
                  'Neighbourhood comparisons',
                  'Buyer demand insights',
                  'Pricing strategy guidance',
                  'Community connections',
                  'Area-specific marketing',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <i className="ri-check-line text-primary-500 text-sm" />
                    <span className="text-foreground-700 font-body text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-1/2">
              <div className="rounded-xl overflow-hidden border border-background-200/70">
                <img
                  src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20a%20peaceful%20suburban%20neighbourhood%20with%20tree-lined%20streets%2C%20well-maintained%20homes%20and%20green%20spaces%2C%20warm%20golden%20afternoon%20light%2C%20professional%20real%20estate%20photography%2C%20clean%20composition%2C%20editorial%20quality&width=800&height=600&seq=contact-service-area-01&orientation=landscape"
                  alt="Suburban neighbourhood aerial view"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="py-14 md:py-20 bg-foreground-950">
        <div className="max-w-[700px] mx-auto px-4 md:px-6 lg:px-10 text-center">
          <h2 className="font-heading text-background-50 text-2xl md:text-3xl lg:text-4xl mb-4">
            Not Ready to Message Just Yet?
          </h2>
          <p className="text-background-100/60 font-body text-sm md:text-base leading-relaxed mb-8">
            Explore Carol's seller resources, read helpful blog articles or request a free home
            valuation to get started on your own terms.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/home-valuation"
              className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-6 py-3.5 rounded-lg text-sm font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-4-line text-base" />
              Request a Free Home Valuation
            </Link>
            <Link
              to="/seller-resources"
              className="inline-flex items-center justify-center gap-2 border border-background-700 text-background-100 px-6 py-3.5 rounded-lg text-sm font-body font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors"
            >
              <i className="ri-book-open-line text-base" />
              Browse Seller Resources
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}